import * as React from 'react';

import { Button, Col } from 'react-bootstrap';
import {
    FormArray,
    FormGenerator,
    FormGroup,
    GroupProps
} from 'react-reactive-form';
import { IcloudDocument, IDocumentType, Ioption } from '../../../models';
import { clearSelectedDocumentID } from '../../../actions/manageDocumentActions';
import {
    saveDocumentTypes,
    setSelectedDocumentTypeID,
    toggleEditDocumentTypeModal
} from '../../../actions/manageDocumentTypeActions';

import { FormUtil } from '../../common/FormUtil';
import { constants } from '../../../constants/constants';
import { initialDocumentType } from '../../../reducers/initialState';
import moment from 'moment';
import { toastr } from 'react-redux-toastr';
import { TFunction } from 'i18next';
import CommonModal from '../../common/CommonModal';
import CommonMobileModal, {
    MobileButtonGroup
} from '../../common/mobile/CommonMobileModal';
import {
    create,
    edit,
    resolveDocumentDateFormState,
    resolveFacilityFormState,
    resolveSelectValue,
    resolveSubmitFacilityID
} from '../utils/ManageDocumentUtils';
import AttachedFile from './AttachedFile';

const uuid4 = require('uuid/v4');

interface Iprops {
    handleCancel: any;
    saveDocumentType: typeof saveDocumentTypes;
    loading: boolean;
    colorButton: string;
    selectedDocument: IcloudDocument;
    parentId: string;
    parentName: string;
    clearSelectedDocumentID: typeof clearSelectedDocumentID;
    updateFormValue: (formValue: { [key: string]: any }) => void;
    setFormValues: (formValues: { [key: string]: any }) => void;
    formValues: { [key: string]: any };
    documentTypeOptions: any[];
    isVersionForm?: boolean;
    toggleEditDocumentTypeModal: typeof toggleEditDocumentTypeModal;
    setSelectedDocumentTypeID: typeof setSelectedDocumentTypeID;
    facilityOptions: Ioption[];
    selectedFacilityID: string;
    canEditDocuments: boolean;
    canAddDocuments: boolean;
    t: TFunction;
    show: boolean;
    title: string;
    className: string;
    isMobile: boolean;
    dispatch: Function;
    isEditForm: boolean;
    isNewForm: boolean;
    documentID?: string;
}

interface IState {
    file: File | '';
}

const EditDocumentLibraryForm: React.FC<Iprops> = (props: Iprops) => {
    const [form, setForm] = React.useState<FormGroup | FormArray>();
    // live handle for closures built before the form mounts (buttonAction)
    const formRef = React.useRef<FormGroup | FormArray>();
    // Scopes the file-input reset below. A page-wide document.querySelector
    // would be fragile and could reach outside this form.
    const formContainerRef = React.useRef<HTMLDivElement>(null);
    const [state, setState] = React.useState<IState>({
        file: ''
    });
    /*
     *    Handle creating a new type
     */
    const handleCreateType = (name: string) => {
        const newType: IDocumentType = {
            ...initialDocumentType,
            id: uuid4(),
            name
        };
        props.saveDocumentType(newType);

        // Creating a type grows documentTypesByID, which changes the fieldConfig
        // memo's documentTypeOptions.length dep. FormGenerator treats a new
        // fieldConfig as a full form rebuild (it nulls this.form and reseeds
        // every control from formState), so everything the user has typed has to
        // be carried through formValues. The facility especially: without it the
        // control silently snaps back to the browsed facility and the document
        // gets misfiled. The memo already seeds name, documentDate and countryID
        // from formValues, so carrying them here preserves them too.
        const liveValues: { [key: string]: any } =
            (formRef.current && (formRef.current.value as any)) || {};

        props.setFormValues({
            ...props.formValues,
            name: liveValues.name,
            documentDate: liveValues.documentDate,
            countryID: liveValues.countryID,
            facilityID: liveValues.facilityID,
            cloudDocumentTypeID: { value: newType.name, label: newType.name }
        });
    };

    /*
     * React cannot reset a file input by rendering: FormUtil.FileInput
     * overrides handler()'s onChange and never writes the file into the form
     * control, so the DOM node is the only thing holding the selection. Without
     * clearing it, removing a file and reselecting the *same* file changes
     * nothing about the input's value, no change event fires, meta.onChange
     * never runs, and Save rejects with "Please select a file" while the modal
     * shows nothing wrong.
     */
    const resetFileInput = () => {
        const input = formContainerRef.current?.querySelector<HTMLInputElement>(
            'input[type="file"]'
        );
        if (input) {
            input.value = '';
        }
    };

    const removeFile = () => {
        setState({ file: '' });
        resetFileInput();
    };

    const onFileChange = (key: string, file: File) => {
        if (file.size > 100000000) {
            toastr.error(
                props.t('manageDocument:fileSizeWarning'),
                '',
                constants.toastrError
            );
            // Leave state.file alone - a rejected pick should be a no-op, so any
            // previously attached file stays attached - but clear the input, or
            // the oversized file sits there invisibly and blocks a later
            // reselect of that same (now shrunk) file.
            //
            // FormUtil.FileInput reads fileInput.files[0].name *after*
            // meta.onChange returns, and clearing value empties that live
            // FileList - so defer the reset out of the handler rather than
            // reaching into the shared control.
            Promise.resolve().then(resetFileInput);
            return;
        }

        setState({ file });
    };

    const fieldConfig = React.useMemo(() => {
        const formValues = props.formValues;

        const disabled = props.canEditDocuments === false;
        const countryDisabled = props.canAddDocuments === false;
        let {
            name,
            cloudDocumentTypeID,
            documentDate,
            filename,
            countryID
        } = props.selectedDocument;

        name = formValues.name || name || props.parentName;
        cloudDocumentTypeID = formValues.cloudDocumentTypeID
            ? formValues.cloudDocumentTypeID.value
            : cloudDocumentTypeID;
        filename = formValues.filename ? formValues.filename : filename;
        documentDate = formValues.documentDate || documentDate;
        countryID = formValues.countryID || countryID;
        const selectedType =
            props.documentTypeOptions.find(
                option =>
                    option.value === cloudDocumentTypeID ||
                    option.label === cloudDocumentTypeID
            ) || '';

        // Which facility the Facility control falls back to on a fresh open.
        // Editing an existing document must show that document's own facility -
        // falling back to the browsed one would silently offer to move it, and a
        // user who never touched the field would move it by saving.
        const facilityFallbackID = props.isEditForm
            ? props.selectedDocument.facilityID
            : props.selectedFacilityID;

        const fieldConfigControls = {
            name: {
                options: {
                    validators: [FormUtil.validators.requiredWithTrim]
                },
                render: FormUtil.TextInput,
                meta: {
                    label: props.parentId
                        ? 'manageDocument:versionName'
                        : 'manageDocument:name',
                    colWidth: 12,
                    type: 'input',
                    autoFocus: true,
                    name: 'document-name'
                },
                formState: { value: name, disabled }
            },
            cloudDocumentTypeID: {
                render: props.canEditDocuments
                    ? FormUtil.CreatableSelectWithButton
                    : FormUtil.CreatableSelect,
                meta: {
                    options: props.documentTypeOptions,
                    label: 'manageDocument:type',
                    subLabel: ' - ' + props.t('manageDocument:typeSubLabel'),
                    colWidth: 12,
                    placeholder: 'manageDocument:selectTypePlaceholder',
                    isMulti: false,
                    handleCreate: handleCreateType,
                    buttonName: props.t('common:edit'),
                    buttonAction: () => {
                        // point the type modal at the currently selected
                        // type so its form opens populated
                        const liveOption =
                            formRef.current &&
                            (formRef.current.value as any).cloudDocumentTypeID;
                        const option = liveOption || selectedType;
                        if (option && option.value) {
                            props.setSelectedDocumentTypeID(option.value);
                        }
                        props.toggleEditDocumentTypeModal();
                    },
                    required: true
                },
                options: { validators: [FormUtil.validators.requiredWithTrim] },
                formState: { value: selectedType, disabled }
            },
            facilityID: {
                render: FormUtil.Select,
                meta: {
                    options: props.facilityOptions,
                    label: 'manageDocument:facility',
                    colWidth: 12,
                    placeholder: 'manageDocument:facilitySearchPlaceholder',
                    name: 'facility',
                    required: true
                },
                options: { validators: [FormUtil.validators.requiredWithTrim] },
                formState: {
                    value: resolveFacilityFormState(
                        // Only trust the live form while the modal is open.
                        // CommonModal unmounts its children on hide but this
                        // component never unmounts and formRef is never
                        // cleared, so reading it unguarded would resurrect a
                        // dead FormGroup's facility on the next open.
                        props.show
                            ? formRef.current &&
                                  (formRef.current.value as any).facilityID
                            : undefined,
                        formValues,
                        props.facilityOptions,
                        facilityFallbackID
                    ),
                    disabled
                }
            },
            documentDate: {
                render: FormUtil.Datetime,
                meta: {
                    label: 'manageDocument:date',
                    colWidth: 12,
                    showTime: false,
                    name: 'document-date',
                    placeholder: 'dd-mmm-yy'
                },
                options: {
                    validators: [
                        FormUtil.validators.requiredWithTrim,
                        FormUtil.validators.isValidMoment
                    ]
                },
                formState: {
                    value: documentDate,
                    disabled
                }
            },
            countryID: {
                render: FormUtil.Select,
                meta: {
                    options: constants.countries,
                    label: 'manageDocument:countryWithHelper',
                    colWidth: 12,
                    placeholder: 'manageDocument:countrySearchPlaceholder',
                    name: 'country',
                    className: 'doc-country-select',
                    // Country is optional, so a mis-selection has to be
                    // undoable. FormUtil.Select defaults isClearable to false.
                    isClearable: true
                },
                formState: { value: countryID, disabled: countryDisabled }
            },
            file: {
                render: FormUtil.FileInput,
                meta: {
                    accept:
                        '.xlsx,.xls,image/*,.doc,.docx,.ppt,.pptx,.txt,.pdf,.zip',
                    type: 'file',
                    label: 'manageDocument:upload',
                    colWidth: 12,
                    name: 'document-file',
                    onChange: onFileChange,
                    fileName: filename
                }
            }
        } as { [key: string]: GroupProps };

        // Add and Edit: "Doc Name" is really a freeform description, so it gets
        // its own label and a multi-line input. Derived rather than edited in
        // place because New Version still shares the `name` control and keeps
        // its single-line "Version Name".
        const descriptionControl: GroupProps = {
            ...fieldConfigControls.name,
            meta: {
                ...fieldConfigControls.name.meta,
                label: 'manageDocument:documentDetailsAndDescription',
                componentClass: 'textarea',
                rows: 4,
                // the shared control sets type: 'input', which FormUtil.TextInput
                // would render as an invalid type="input" on a textarea
                type: undefined,
                autoFocus: undefined
            }
        };

        // Add Document only: a new document is almost always dated today, so
        // default it. Derived rather than edited in place because New Version
        // shares the documentDate control and keeps starting empty.
        const documentDateControl: GroupProps = {
            ...fieldConfigControls.documentDate,
            formState: {
                value: resolveDocumentDateFormState(documentDate),
                disabled
            }
        };

        // FormGenerator renders controls in key insertion order, so this object
        // literally is the Add Document field order. Typed with an index
        // signature (which the previous `{ ...fieldConfigControls }` spread
        // inferred for free) so the Edit and Version branches below can still
        // reassign it with their own shorter control sets.
        let fieldsConfig: { controls: { [key: string]: GroupProps } } = {
            controls: {
                cloudDocumentTypeID: fieldConfigControls.cloudDocumentTypeID,
                name: descriptionControl,
                facilityID: fieldConfigControls.facilityID,
                documentDate: documentDateControl,
                countryID: fieldConfigControls.countryID,
                file: fieldConfigControls.file
            }
        };

        // don't show the file control when editing.  Users must add a new version in order to add a new file.
        if (props.isEditForm) {
            // Same key-insertion-order rule as the Add object above: Doc Type,
            // then the tall description, then Facility at the bottom.
            fieldsConfig = {
                controls: {
                    cloudDocumentTypeID:
                        fieldConfigControls.cloudDocumentTypeID,
                    name: descriptionControl,
                    facilityID: fieldConfigControls.facilityID
                }
            };
        } else if (props.isVersionForm) {
            fieldsConfig = {
                controls: {
                    name: fieldConfigControls.name,
                    documentDate: fieldConfigControls.documentDate,
                    file: fieldConfigControls.file
                }
            };
        }

        return FormUtil.translateForm(fieldsConfig, props.t);
        // eslint-disable-next-line
    }, [
        props.selectedDocument,
        props.documentTypeOptions.length,
        props.facilityOptions.length,
        props.selectedFacilityID,
        props.isVersionForm,
        props.isEditForm,
        props.isNewForm,
        props.parentId
    ]);

    // formRef outlives the form: FormGenerator unmounts with CommonModal, but
    // this component does not, so the ref would keep pointing at a dead
    // FormGroup. The facility control reads it (gated on props.show) to survive
    // form rebuilds, so it has to be dropped on close or a reopened modal could
    // resurrect the previous session's facility. state.file has the same
    // problem: the same useState serves Add, Edit and New Version as redux
    // flips props, and nothing else clears it on Cancel, so a file attached
    // and then abandoned would render as attached (and get uploaded) the next
    // time this modal opens, in whichever mode. An effect on show catches
    // every close path - dismiss, Cancel, and the redux toggle fired from
    // create()/edit().
    React.useEffect(() => {
        if (!props.show) {
            formRef.current = undefined;
            // Bail out of the update when there's nothing to clear, so this
            // doesn't force a render on every close (including mount) - React
            // skips re-rendering when a state updater returns the same object.
            setState(prev => (prev.file === '' ? prev : { file: '' }));
            // The desktop modal keeps its children mounted through the 300ms
            // exit transition, so a fast reopen can land on this same input
            // node still holding the old file. No-op once unmounted.
            resetFileInput();
        }
    }, [props.show]);

    React.useEffect(() => {
        if (props.selectedDocument.documentID !== '') {
            props.setFormValues({
                documentID: props.selectedDocument.documentID
            });
        }
        // eslint-disable-next-line
    }, [props.selectedDocument]);

    const handleSubmit = (e: React.MouseEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!form) {
            return;
        }

        if (form.status === 'INVALID') {
            form.markAsSubmitted();
            toastr.error(
                props.t('toastMessage:invalidFormSubmission'),
                '',
                constants.toastrError
            );
            return;
        }

        let submitFacilityID: string;
        try {
            submitFacilityID = resolveSubmitFacilityID(
                // Add and Edit both carry a Facility control; New Version does not
                props.isNewForm || props.isEditForm,
                form.value,
                props.selectedFacilityID
            );
        } catch (error) {
            // resolveSubmitFacilityID throws when the Add form has no facility.
            // Unreachable on a valid form, but an uncaught throw in an onSubmit
            // handler just looks like a dead Save button - every other guard in
            // this function toastrs before returning, so match that.
            console.error('error resolving document facility', error);
            toastr.error(
                props.t('toastMessage:invalidFormSubmission'),
                '',
                constants.toastrError
            );
            return;
        }

        let formData = {
            ...form.value,
            facilityID: submitFacilityID,
            cloudDocumentTypeID: form.value.cloudDocumentTypeID
                ? form.value.cloudDocumentTypeID.value
                : '',
            documentDate: form.value.documentDate
                ? moment.utc(form.value.documentDate).toISOString()
                : '',
            // Only the Add form has a Country control, and it is clearable -
            // which yields null, and FormData would send that as "null". The
            // conditional spread keeps the key absent for Edit and New Version
            // rather than adding an empty one they never sent before.
            ...(form.value.countryID === undefined
                ? {}
                : { countryID: resolveSelectValue(form.value.countryID) })
        };

        if (props.selectedDocument.documentID) {
            formData = {
                ...form.value,
                facilityID: submitFacilityID,
                cloudDocumentTypeID: form.value.cloudDocumentTypeID.value
            };
        }

        if (state.file) {
            formData['file'] = state.file;
        } else if (!props.selectedDocument.documentID) {
            toastr.error(
                'Error',
                'Please select a file.',
                constants.toastrError
            );
            return;
        }

        if (props.isVersionForm) {
            formData['documentID'] = props.documentID;
        } else {
            formData['id'] = props.selectedDocument.id;
        }

        if (formData.id) {
            formData.file = undefined;
            edit(
                formData,
                props.selectedDocument,
                props.parentId,
                props.dispatch
            )
                .then(e => {
                    // The modal closes via TOGGLE_MODAL_EDIT_DOCUMENT inside
                    // create()/edit(), which does not clear
                    // documentFormValues. Without this, a reopened modal seeds
                    // from the last save - including a stale facility that
                    // would silently override the browsed-facility default.
                    // (state.file is cleared by the close effect above, since
                    // that same toggle also flips props.show.)
                    props.setFormValues({});
                })
                .catch(e => {
                    console.error('error creating doc', e);
                });
        } else {
            create(formData, props.dispatch, props.parentId)
                .then(e => {
                    props.setFormValues({});
                    // ADD_DOCUMENT_SUCCESS inserts into documentsByID without
                    // checking facility, so a document saved to another
                    // facility would linger in this facility's list. Reload so
                    // the facility-scoped refetch is authoritative.
                    if (formData.facilityID !== props.selectedFacilityID) {
                        window.location.reload();
                    }
                })
                .catch(e => {
                    console.error('error creating doc', e);
                });
        }
    };

    const setFormContent = (formGroup: FormGroup | FormArray) => {
        formGroup.meta = {
            loading: props.loading
        };
        formRef.current = formGroup;
        setForm(formGroup);
    };

    const handleClose = () => {
        props.handleCancel();
        props.setFormValues({});
    };

    const Buttons = () => {
        return (
            <>
                {' '}
                <Button
                    bsStyle="default"
                    type="button"
                    className="pull-left"
                    onClick={() => {
                        props.handleCancel();
                        props.setFormValues({});
                    }}
                >
                    {props.t('common:cancel')}
                </Button>
                <Button
                    bsStyle={props.colorButton}
                    type="submit"
                    disabled={props.loading || props.canEditDocuments === false}
                >
                    {props.t('common:save')}
                </Button>
            </>
        );
    };

    if (props.isMobile) {
        return (
            <form onSubmit={handleSubmit} className="clearfix beacon-form">
                <CommonMobileModal
                    footer={
                        <MobileButtonGroup>
                            <Buttons />
                        </MobileButtonGroup>
                    }
                    show={props.show}
                    onHide={handleClose}
                    className={'edit-document-mobile-modal'}
                    title={props.title}
                >
                    {/* clearfix: contains the floated .col-xs-12 field columns
                    FormGenerator renders, so they don't collapse the div to
                    zero height and let AttachedFile overlap the fields. */}
                    <div ref={formContainerRef} className="clearfix">
                        <FormGenerator
                            onMount={setFormContent}
                            fieldConfig={fieldConfig}
                        />
                        <AttachedFile
                            file={state.file}
                            onRemove={removeFile}
                            t={props.t}
                        />
                    </div>
                </CommonMobileModal>
            </form>
        );
    }

    return (
        <CommonModal
            show={props.show}
            onHide={handleClose}
            className={props.className}
            title={props.title}
        >
            <form onSubmit={handleSubmit} className="clearfix beacon-form">
                {/* clearfix: contains the floated .col-xs-12 field columns
                FormGenerator renders, so they don't collapse the div to zero
                height and let the form-buttons Col below overlap the fields. */}
                <div ref={formContainerRef} className="clearfix">
                    <FormGenerator
                        onMount={setFormContent}
                        fieldConfig={fieldConfig}
                    />
                    <AttachedFile
                        file={state.file}
                        onRemove={removeFile}
                        t={props.t}
                    />
                </div>
                <Col xs={12} className="form-buttons text-right">
                    <Buttons />
                </Col>
            </form>
        </CommonModal>
    );
};

export default EditDocumentLibraryForm;
